// src/pages/CourierManagement.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Building2,
  GitBranch,
  MapPin,
  FileText,
  PackageCheck,
  History,
  Plus,
  Search,
  CheckCircle,
  ExternalLink,
  Phone,
  Mail,
  Edit2,
  RefreshCw,
  Power,
  ChevronLeft,
  ChevronRight,
  Eye,
  Map as MapIcon
} from 'lucide-react';
import Layout from '../component/Layout';
import './CourierManagement.css';
import stateDistrictData from './StatenDistrict.json';

// Subcomponents & Modals
import CourierFormModal from './courier/CourierFormModal';
import BranchFormModal from './courier/BranchFormModal';
import BranchDistrictModal from './courier/BranchDistrictModal';
import DocumentManagerModal from './courier/DocumentManagerModal';
import DistrictDetailModal from './courier/DistrictDetailModal';
import OrderAssignmentSimulator from './courier/OrderAssignmentSimulator';

// Service API
import {
  getCouriers,
  createCourier,
  updateCourier,
  toggleCourierStatus,
  getAllBranchesAcrossCouriers,
  createBranch,
  updateBranch,
  toggleBranchStatus,
  getAllServiceAreas,
  assignBranchDistricts,
  removeBranchDistricts,
  getAuditLogs,
  resolveEligibleCourierBranchesForDistrict,
  repairServiceAreasInFirestore,
  normalizeServiceArea,
  COURIERS_COLLECTION,
  SERVICE_AREAS_COLLECTION
} from '../services/courierService';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';

const CourierManagement = () => {
  // ─────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('couriers'); // 'couriers' | 'branches' | 'coverage' | 'simulator' | 'audit'

  // Master Data (Real data only from Firestore, initialized empty)
  const [couriers, setCouriers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true); // eslint-disable-line no-unused-vars

  // Filters & Search
  const [courierSearch, setCourierSearch] = useState('');
  const [courierStatusFilter, setCourierStatusFilter] = useState('ALL'); // 'ALL' | 'ACTIVE' | 'INACTIVE'
  const [courierPage, setCourierPage] = useState(1);
  const itemsPerPage = 8;

  const [branchSearch, setBranchSearch] = useState('');
  const [branchCourierFilter, setBranchCourierFilter] = useState('ALL');
  const [branchStatusFilter, setBranchStatusFilter] = useState('ALL');
  const [branchPage, setBranchPage] = useState(1);

  // Coverage Tab State
  const [coverageState, setCoverageState] = useState('Odisha');
  const [coverageSearch, setCoverageSearch] = useState('');

  // Modals State
  const [isCourierModalOpen, setIsCourierModalOpen] = useState(false);
  const [editingCourier, setEditingCourier] = useState(null);

  const [isBranchModalOpen, setIsBranchModalOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [branchCourierTargetId, setBranchCourierTargetId] = useState('');

  const [isDistrictModalOpen, setIsDistrictModalOpen] = useState(false);
  const [targetBranchForDistricts, setTargetBranchForDistricts] = useState(null);

  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [docModalEntity, setDocModalEntity] = useState(null); // { type: 'COURIER'|'BRANCH', id, name, docs, courierId }

  const [isDistrictDetailOpen, setIsDistrictDetailOpen] = useState(false);
  const [selectedDistrictDetail, setSelectedDistrictDetail] = useState({
    districtName: '',
    stateName: '',
    branches: []
  });

  // Toast feedback
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ─────────────────────────────────────────────
  // DATA FETCHING & SYNCHRONIZATION
  // ─────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // 0. Auto-heal any legacy service areas in Firestore
      repairServiceAreasInFirestore().catch((e) => console.warn('Auto-repair service areas error:', e));

      // 1. Fetch Couriers from Firestore
      const firestoreCouriers = await getCouriers();
      if (firestoreCouriers && firestoreCouriers.length > 0) {
        setCouriers(firestoreCouriers);
        // Fetch subcollection branches
        const allBranches = await getAllBranchesAcrossCouriers(firestoreCouriers);
        setBranches(allBranches);
      } else {
        setCouriers([]);
        setBranches([]);
      }

      // 2. Fetch Service Areas
      const saList = await getAllServiceAreas();
      setServiceAreas(saList || []);

      // 3. Fetch Audit Logs
      const logs = await getAuditLogs(50);
      setAuditLogs(logs || []);
    } catch (err) {
      console.warn('Load master data error:', err);
      setCouriers([]);
      setBranches([]);
      setServiceAreas([]);
      setAuditLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();

    // Listen to real-time changes on couriers collection
    let unsubscribeCouriers;
    let unsubscribeServiceAreas;
    try {
      unsubscribeCouriers = onSnapshot(
        collection(db, COURIERS_COLLECTION),
        async (snapshot) => {
          const map = new Map();
          snapshot.forEach((d) => {
            map.set(d.id, { ...d.data(), courierId: d.id });
          });
          const list = Array.from(map.values());
          setCouriers(list);
          if (list.length > 0) {
            const allBranches = await getAllBranchesAcrossCouriers(list);
            const bMap = new Map();
            allBranches.forEach((b) => {
              if (b && b.branchId) bMap.set(b.branchId, b);
            });
            setBranches(Array.from(bMap.values()));
          } else {
            setBranches([]);
          }
        },
        (error) => {
          console.warn('Real-time couriers listener error:', error);
        }
      );

      unsubscribeServiceAreas = onSnapshot(
        collection(db, SERVICE_AREAS_COLLECTION),
        (snapshot) => {
          const map = new Map();
          snapshot.forEach((d) => {
            const norm = normalizeServiceArea(d.id, d.data());
            map.set(d.id, norm);
          });
          setServiceAreas(Array.from(map.values()));
        },
        (error) => {
          console.warn('Real-time service areas listener error:', error);
        }
      );
    } catch (e) {
      // Offline fallback
    }

    return () => {
      if (unsubscribeCouriers) unsubscribeCouriers();
      if (unsubscribeServiceAreas) unsubscribeServiceAreas();
    };
  }, [loadData]);

  // Guaranteed Unique Collections
  const uniqueCouriers = useMemo(() => {
    const map = new Map();
    (couriers || []).forEach((c) => {
      if (c && c.courierId) {
        map.set(c.courierId, c);
      }
    });
    return Array.from(map.values());
  }, [couriers]);

  const uniqueBranches = useMemo(() => {
    const map = new Map();
    (branches || []).forEach((b) => {
      if (b && b.branchId) {
        map.set(b.branchId, b);
      }
    });
    return Array.from(map.values());
  }, [branches]);

  // ─────────────────────────────────────────────
  // SUMMARY KPI COMPUTATIONS
  // ─────────────────────────────────────────────
  const totalCouriers = uniqueCouriers.length;
  const activeCouriers = uniqueCouriers.filter((c) => c.active !== false).length;
  const totalBranches = uniqueBranches.length;

  // Set of all distinct districts in India covered by at least 1 active branch of an active courier
  const coveredDistrictsSet = useMemo(() => {
    const set = new Set();
    serviceAreas.forEach((sa) => {
      if (sa.active !== false) {
        // check if branch & courier active
        const br = uniqueBranches.find((b) => b.branchId === sa.branchId);
        const cr = uniqueCouriers.find((c) => c.courierId === sa.courierId);
        if (br && br.active !== false && cr && cr.active !== false) {
          const st = (sa.stateName || sa.stateCode || '').trim();
          const dt = (sa.districtName || sa.districtCode || '').trim();
          if (st && dt) {
            set.add(`${st}__${dt}`);
          }
        }
      }
    });
    return set;
  }, [serviceAreas, uniqueBranches, uniqueCouriers]);

  // Total districts in India from dataset
  const totalIndiaDistrictsCount = useMemo(() => {
    let count = 0;
    (stateDistrictData?.states || []).forEach((st) => {
      count += (st.districts || []).length;
    });
    return count;
  }, []);

  const coveredDistrictsCount = coveredDistrictsSet.size;
  const uncoveredDistrictsCount = Math.max(0, totalIndiaDistrictsCount - coveredDistrictsCount);

  // ─────────────────────────────────────────────
  // COURIER ACTIONS
  // ─────────────────────────────────────────────
  const handleSaveCourier = async (courierData) => {
    try {
      if (editingCourier) {
        // Update
        await updateCourier(
          editingCourier.courierId,
          courierData,
          editingCourier,
          'SUPERADMIN'
        );
        setCouriers((prev) =>
          prev.map((c) =>
            c.courierId === editingCourier.courierId ? { ...c, ...courierData } : c
          )
        );
        showToast(`Updated courier company "${courierData.name}"`);
      } else {
        // Create
        const created = await createCourier(courierData, 'SUPERADMIN');
        setCouriers((prev) => {
          // If onSnapshot already added it, replace; otherwise prepend
          if (prev.some((c) => c.courierId === created.courierId)) {
            return prev.map((c) => (c.courierId === created.courierId ? created : c));
          }
          return [created, ...prev];
        });
        showToast(`Created courier partner "${created.name}" (${created.courierId})`);
      }
    } catch (err) {
      console.error('Error saving courier:', err);
      if (editingCourier) {
        setCouriers((prev) =>
          prev.map((c) =>
            c.courierId === editingCourier.courierId ? { ...c, ...courierData } : c
          )
        );
        showToast(`Updated courier "${courierData.name}"`);
      } else {
        const fakeId = `CR-${Date.now().toString().slice(-4)}`;
        const localCreated = { ...courierData, courierId: fakeId, branches: [], documents: [] };
        setCouriers((prev) => {
          if (prev.some((c) => c.courierId === localCreated.courierId)) return prev;
          return [localCreated, ...prev];
        });
        showToast(`Created courier partner "${courierData.name}"`);
      }
    }
  };

  const handleToggleCourierActive = async (courier) => {
    try {
      const nextStatus = await toggleCourierStatus(
        courier.courierId,
        courier.active !== false,
        courier.name,
        'SUPERADMIN'
      );
      setCouriers((prev) =>
        prev.map((c) => (c.courierId === courier.courierId ? { ...c, active: nextStatus } : c))
      );
      showToast(
        `${nextStatus ? 'Activated' : 'Deactivated'} courier company "${courier.name}"`
      );
    } catch (err) {
      console.error('Toggle courier error:', err);
      const next = !(courier.active !== false);
      setCouriers((prev) =>
        prev.map((c) => (c.courierId === courier.courierId ? { ...c, active: next } : c))
      );
      showToast(`${next ? 'Activated' : 'Deactivated'} courier (Local state)`);
    }
  };

  // ─────────────────────────────────────────────
  // BRANCH ACTIONS
  // ─────────────────────────────────────────────
  const handleSaveBranch = async (branchData) => {
    const parentCourier = couriers.find((c) => c.courierId === branchData.courierId);
    try {
      if (editingBranch) {
        await updateBranch(
          branchData.courierId,
          editingBranch.branchId,
          branchData,
          editingBranch,
          'SUPERADMIN'
        );
        setBranches((prev) =>
          prev.map((b) =>
            b.branchId === editingBranch.branchId ? { ...b, ...branchData } : b
          )
        );
        showToast(`Updated branch "${branchData.branchName}"`);
      } else {
        const created = await createBranch(
          branchData.courierId,
          branchData,
          parentCourier?.name || '',
          'SUPERADMIN'
        );
        const enriched = {
          ...created,
          courierName: parentCourier?.name || branchData.courierId,
          courierActive: parentCourier?.active !== false
        };
        setBranches((prev) => {
          if (prev.some((b) => b.branchId === enriched.branchId)) {
            return prev.map((b) => (b.branchId === enriched.branchId ? enriched : b));
          }
          return [enriched, ...prev];
        });
        showToast(`Created authorized branch "${created.branchName}" (${created.branchCode})`);
      }
    } catch (err) {
      console.error('Save branch error:', err);
      if (editingBranch) {
        setBranches((prev) =>
          prev.map((b) =>
            b.branchId === editingBranch.branchId ? { ...b, ...branchData } : b
          )
        );
        showToast(`Updated branch (Local state)`);
      } else {
        const fakeId = `BR-${Date.now().toString().slice(-4)}`;
        const localCreated = {
          ...branchData,
          branchId: fakeId,
          courierName: parentCourier?.name || branchData.courierId,
          documents: []
        };
        setBranches((prev) => {
          if (prev.some((b) => b.branchId === localCreated.branchId)) return prev;
          return [localCreated, ...prev];
        });
        showToast(`Created branch "${branchData.branchName}"`);
      }
    }
  };

  const handleToggleBranchActive = async (branch) => {
    try {
      const nextStatus = await toggleBranchStatus(
        branch.courierId,
        branch.branchId,
        branch.active !== false,
        branch.branchName,
        'SUPERADMIN'
      );
      setBranches((prev) =>
        prev.map((b) => (b.branchId === branch.branchId ? { ...b, active: nextStatus } : b))
      );
      showToast(
        `${nextStatus ? 'Activated' : 'Deactivated'} authorized branch "${branch.branchName}"`
      );
    } catch (err) {
      const next = !(branch.active !== false);
      setBranches((prev) =>
        prev.map((b) => (b.branchId === branch.branchId ? { ...b, active: next } : b))
      );
      showToast(`${next ? 'Activated' : 'Deactivated'} branch (Local state)`);
    }
  };

  // ─────────────────────────────────────────────
  // SERVICE AREA ASSIGNMENT ACTIONS
  // ─────────────────────────────────────────────
  const handleSaveDistrictAssignments = async ({
    courierId,
    branchId,
    stateName,
    stateCode,
    toAssign,
    toRemove
  }) => {
    try {
      if (toAssign.length > 0) {
        await assignBranchDistricts({
          courierId,
          branchId,
          stateCode,
          stateName,
          districtsToAssign: toAssign,
          actor: 'SUPERADMIN'
        });
      }
      if (toRemove.length > 0) {
        await removeBranchDistricts({
          courierId,
          branchId,
          stateCode,
          districtCodesToRemove: toRemove,
          actor: 'SUPERADMIN'
        });
      }
    } catch (err) {
      console.warn('Firestore service area batch write:', err);
    }

    // Optimistic local state update — always run so UI is instant.
    // Uses the same ID format as generateServiceAreaId() so that when
    // onSnapshot fires and replaces serviceAreas from Firestore, the Map
    // de-duplication (keyed by serviceAreaId) removes the local copy cleanly.
    setServiceAreas((prev) => {
      // 1. Remove districts that were unchecked
      let updated = prev.filter(
        (sa) =>
          !(
            sa.branchId === branchId &&
            (sa.stateName?.toLowerCase() === stateName?.toLowerCase() ||
             sa.stateCode?.toLowerCase() === stateCode?.toLowerCase()) &&
            toRemove.includes(sa.districtName || sa.districtCode)
          )
      );

      // 2. Add newly checked districts — skip if already present (prevents double-count)
      toAssign.forEach((item) => {
        const distCode = (item.districtCode || item.districtName).replace(/\s+/g, '_').toUpperCase();
        const cleanState = (stateCode || stateName).replace(/\s+/g, '_').toUpperCase();
        const saId = `${courierId}_${branchId}_${cleanState}_${distCode}`;
        if (!updated.some((sa) => sa.serviceAreaId === saId)) {
          updated.push({
            serviceAreaId: saId,
            courierId,
            branchId,
            stateName,
            stateCode,
            districtName: item.districtName,
            districtCode: item.districtCode || item.districtName,
            active: true
          });
        }
      });

      return updated;
    });

    showToast(
      `Updated district assignments: +${toAssign.length} assigned, -${toRemove.length} removed`
    );

  };

  // ─────────────────────────────────────────────
  // DOCUMENT MANAGEMENT ACTIONS
  // ─────────────────────────────────────────────
  const handleSaveDocuments = async (updatedDocs) => {
    if (!docModalEntity) return;

    if (docModalEntity.type === 'COURIER') {
      try {
        await updateCourier(
          docModalEntity.id,
          { documents: updatedDocs },
          { documents: docModalEntity.docs },
          'SUPERADMIN'
        );
      } catch (err) {
        console.warn('Doc update error:', err);
      }
      setCouriers((prev) =>
        prev.map((c) =>
          c.courierId === docModalEntity.id ? { ...c, documents: updatedDocs } : c
        )
      );
      showToast(`Updated courier business documents (${updatedDocs.length} total)`);
    } else if (docModalEntity.type === 'BRANCH') {
      try {
        await updateBranch(
          docModalEntity.courierId,
          docModalEntity.id,
          { documents: updatedDocs },
          { documents: docModalEntity.docs },
          'SUPERADMIN'
        );
      } catch (err) {
        console.warn('Doc update error:', err);
      }
      setBranches((prev) =>
        prev.map((b) =>
          b.branchId === docModalEntity.id ? { ...b, documents: updatedDocs } : b
        )
      );
      showToast(`Updated branch documents (${updatedDocs.length} total)`);
    }
  };

  // ─────────────────────────────────────────────
  // COVERAGE DRILL-DOWN MODAL
  // ─────────────────────────────────────────────
  const handleOpenDistrictCoverageDetail = async (distName, stName) => {
    // Resolve which active courier branches serve this district
    const results = await resolveEligibleCourierBranchesForDistrict({
      districtName: distName,
      stateName: stName,
      couriersCache: couriers,
      branchesCache: branches,
      serviceAreasCache: serviceAreas
    });

    setSelectedDistrictDetail({
      districtName: distName,
      stateName: stName,
      branches: results
    });
    setIsDistrictDetailOpen(true);
  };

  // ─────────────────────────────────────────────
  // FILTERED LISTS & COMPUTATIONS
  // ─────────────────────────────────────────────
  // 1. Couriers Tab
  const filteredCouriers = useMemo(() => {
    return uniqueCouriers.filter((c) => {
      const matchesSearch =
        !courierSearch.trim() ||
        c.name.toLowerCase().includes(courierSearch.toLowerCase()) ||
        c.contactPerson?.toLowerCase().includes(courierSearch.toLowerCase()) ||
        c.email?.toLowerCase().includes(courierSearch.toLowerCase()) ||
        c.business?.gstNumber?.toLowerCase().includes(courierSearch.toLowerCase());

      const matchesStatus =
        courierStatusFilter === 'ALL' ||
        (courierStatusFilter === 'ACTIVE' && c.active !== false) ||
        (courierStatusFilter === 'INACTIVE' && c.active === false);

      return matchesSearch && matchesStatus;
    });
  }, [uniqueCouriers, courierSearch, courierStatusFilter]);

  const paginatedCouriers = useMemo(() => {
    const start = (courierPage - 1) * itemsPerPage;
    return filteredCouriers.slice(start, start + itemsPerPage);
  }, [filteredCouriers, courierPage]);

  // Branch count & district count per courier
  const courierStatsMap = useMemo(() => {
    const map = {};
    uniqueCouriers.forEach((c) => {
      const cBranches = uniqueBranches.filter((b) => b.courierId === c.courierId);
      const branchIds = new Set(cBranches.map((b) => b.branchId));
      const cDistricts = new Set(
        serviceAreas
          .filter((sa) => branchIds.has(sa.branchId) && sa.active !== false)
          .map((sa) => {
            const st = (sa.stateName || sa.stateCode || '').trim();
            const dt = (sa.districtName || sa.districtCode || '').trim();
            return st && dt ? `${st}__${dt}` : null;
          })
          .filter(Boolean)
      );
      map[c.courierId] = {
        branchCount: cBranches.length,
        districtCount: cDistricts.size
      };
    });
    return map;
  }, [uniqueCouriers, uniqueBranches, serviceAreas]);

  // 2. Branches Tab
  const filteredBranches = useMemo(() => {
    return uniqueBranches.filter((b) => {
      const matchesSearch =
        !branchSearch.trim() ||
        b.branchName.toLowerCase().includes(branchSearch.toLowerCase()) ||
        b.branchCode?.toLowerCase().includes(branchSearch.toLowerCase()) ||
        b.contactPerson?.toLowerCase().includes(branchSearch.toLowerCase()) ||
        b.contactNumber?.toLowerCase().includes(branchSearch.toLowerCase()) ||
        b.address?.city?.toLowerCase().includes(branchSearch.toLowerCase()) ||
        b.address?.districtName?.toLowerCase().includes(branchSearch.toLowerCase()) ||
        b.address?.districtCode?.toLowerCase().includes(branchSearch.toLowerCase());

      const matchesCourier =
        branchCourierFilter === 'ALL' || b.courierId === branchCourierFilter;

      const matchesStatus =
        branchStatusFilter === 'ALL' ||
        (branchStatusFilter === 'ACTIVE' && b.active !== false) ||
        (branchStatusFilter === 'INACTIVE' && b.active === false);

      return matchesSearch && matchesCourier && matchesStatus;
    });
  }, [uniqueBranches, branchSearch, branchCourierFilter, branchStatusFilter]);

  const paginatedBranches = useMemo(() => {
    const start = (branchPage - 1) * itemsPerPage;
    return filteredBranches.slice(start, start + itemsPerPage);
  }, [filteredBranches, branchPage]);

  // District count per branch
  const branchDistrictsCountMap = useMemo(() => {
    const map = {};
    serviceAreas.forEach((sa) => {
      if (sa.active !== false) {
        map[sa.branchId] = (map[sa.branchId] || 0) + 1;
      }
    });
    return map;
  }, [serviceAreas]);

  // 3. Service Coverage Tab Computations
  const coverageStateDistricts = useMemo(() => {
    const found = (stateDistrictData?.states || []).find(
      (s) => s.state.toLowerCase() === coverageState.toLowerCase()
    );
    return found ? found.districts : [];
  }, [coverageState]);

  const coverageDistrictBranchCounts = useMemo(() => {
    const map = {};
    coverageStateDistricts.forEach((d) => {
      map[d] = 0;
    });

    const normCovState = (coverageState || '').trim().toLowerCase();

    serviceAreas.forEach((sa) => {
      const saState = (sa.stateName || sa.stateCode || '').trim().toLowerCase();
      if (
        sa.active !== false &&
        (saState === normCovState || saState.replace(/\s+/g, '_') === normCovState.replace(/\s+/g, '_'))
      ) {
        const br = branches.find((b) => b.branchId === sa.branchId);
        const cr = couriers.find((c) => c.courierId === sa.courierId);
        if (br && br.active !== false && cr && cr.active !== false) {
          const rawDist = (sa.districtName || sa.districtCode || '').trim().toLowerCase();
          const matchedDistrict = coverageStateDistricts.find(
            (d) => d.toLowerCase() === rawDist || d.replace(/\s+/g, '_').toLowerCase() === rawDist
          );
          if (matchedDistrict && map[matchedDistrict] !== undefined) {
            map[matchedDistrict] += 1;
          }
        }
      }
    });
    return map;
  }, [coverageStateDistricts, serviceAreas, branches, couriers, coverageState]);

  const filteredCoverageDistricts = useMemo(() => {
    if (!coverageSearch.trim()) return coverageStateDistricts;
    return coverageStateDistricts.filter((d) =>
      d.toLowerCase().includes(coverageSearch.toLowerCase())
    );
  }, [coverageStateDistricts, coverageSearch]);

  const stateCoveredCount = useMemo(() => {
    return Object.values(coverageDistrictBranchCounts).filter((cnt) => cnt > 0).length;
  }, [coverageDistrictBranchCounts]);

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────
  return (
    <Layout pageTitle="Courier Management">
      <div className="cm-container">
        {/* Toast Notification */}
        {toast && (
          <div
            style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              background: toast.type === 'error' ? '#ef4444' : '#166534',
              color: '#ffffff',
              padding: '12px 20px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 2000,
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            {toast.message}
          </div>
        )}

        {/* Page Header */}
        <div className="cm-page-header">
          <div className="cm-page-title-group">
            <h1>Courier Management</h1>
            <p className="cm-page-subtitle">
              Manage courier partners, authorized branches and district-wise service coverage.
            </p>
          </div>

          <div className="cm-page-actions">
            <button
              type="button"
              className="cm-btn-secondary"
              onClick={loadData}
              title="Refresh Data"
            >
              <RefreshCw size={15} /> Refresh
            </button>
            <button
              type="button"
              className="cm-btn-secondary"
              onClick={() => {
                setEditingBranch(null);
                setBranchCourierTargetId(uniqueCouriers[0]?.courierId || '');
                setIsBranchModalOpen(true);
              }}
            >
              <GitBranch size={15} />  Add Branch
            </button>
            <button
              type="button"
              className="cm-btn-primary"
              onClick={() => {
                setEditingCourier(null);
                setIsCourierModalOpen(true);
              }}
            >
              <Plus size={16} />  Add Courier
            </button>
          </div>
        </div>

        {/* Summary KPI Cards */}
        <div className="cm-kpi-grid">
          <div className="cm-kpi-card">
            <div className="cm-kpi-content">
              <span className="cm-kpi-title">Total Couriers</span>
              <span className="cm-kpi-value">{totalCouriers}</span>
              <span className="cm-kpi-sub">{activeCouriers} active partners</span>
            </div>
            <div className="cm-kpi-icon-badge cm-kpi-gold">
              <Building2 size={24} />
            </div>
          </div>

          <div className="cm-kpi-card">
            <div className="cm-kpi-content">
              <span className="cm-kpi-title">Active Couriers</span>
              <span className="cm-kpi-value" style={{ color: '#059669' }}>
                {activeCouriers}
              </span>
              <span className="cm-kpi-sub">
                {totalCouriers - activeCouriers} inactive / paused
              </span>
            </div>
            <div className="cm-kpi-icon-badge cm-kpi-green">
              <CheckCircle size={24} />
            </div>
          </div>

          <div className="cm-kpi-card">
            <div className="cm-kpi-content">
              <span className="cm-kpi-title">Total Branches</span>
              <span className="cm-kpi-value">{totalBranches}</span>
              <span className="cm-kpi-sub">Authorized hub locations</span>
            </div>
            <div className="cm-kpi-icon-badge cm-kpi-blue">
              <GitBranch size={24} />
            </div>
          </div>

          <div className="cm-kpi-card">
            <div className="cm-kpi-content">
              <span className="cm-kpi-title">Covered Districts</span>
              <span className="cm-kpi-value" style={{ color: '#2563eb' }}>
                {coveredDistrictsCount}
              </span>
              <span className="cm-kpi-sub">Serviceable delivery zones</span>
            </div>
            <div className="cm-kpi-icon-badge cm-kpi-purple">
              <MapPin size={24} />
            </div>
          </div>

          <div className="cm-kpi-card">
            <div className="cm-kpi-content">
              <span className="cm-kpi-title">Uncovered Districts</span>
              <span className="cm-kpi-value" style={{ color: '#d97706' }}>
                {uncoveredDistrictsCount}
              </span>
              <span className="cm-kpi-sub">Across India Master list</span>
            </div>
            <div className="cm-kpi-icon-badge cm-kpi-amber">
              <MapIcon size={24} />
            </div>
          </div>
        </div>

        {/* Primary Tabs */}
        <div className="cm-tabs-bar">
          <button
            type="button"
            className={`cm-tab-btn ${activeTab === 'couriers' ? 'active' : ''}`}
            onClick={() => setActiveTab('couriers')}
          >
            <Building2 size={16} /> Couriers
            <span className="cm-tab-count">{uniqueCouriers.length}</span>
          </button>
          <button
            type="button"
            className={`cm-tab-btn ${activeTab === 'branches' ? 'active' : ''}`}
            onClick={() => setActiveTab('branches')}
          >
            <GitBranch size={16} /> Authorized Branches
            <span className="cm-tab-count">{uniqueBranches.length}</span>
          </button>
          <button
            type="button"
            className={`cm-tab-btn ${activeTab === 'coverage' ? 'active' : ''}`}
            onClick={() => setActiveTab('coverage')}
          >
            <MapPin size={16} /> Service Coverage
            <span className="cm-tab-count">{coverageStateDistricts.length}</span>
          </button>
          <button
            type="button"
            className={`cm-tab-btn ${activeTab === 'simulator' ? 'active' : ''}`}
            onClick={() => setActiveTab('simulator')}
          >
            <PackageCheck size={16} /> Order Assignment Simulator
          </button>
          <button
            type="button"
            className={`cm-tab-btn ${activeTab === 'audit' ? 'active' : ''}`}
            onClick={() => setActiveTab('audit')}
          >
            <History size={16} /> Audit History
          </button>
        </div>

        {/* ═════════════════════════════════════════
            TAB 1: COURIERS LIST
            ═════════════════════════════════════════ */}
        {activeTab === 'couriers' && (
          <div className="cm-card">
            <div className="cm-card-header">
              <div>
                <h3 className="cm-card-title">Courier Companies</h3>
                <p className="cm-card-subtitle">
                  Master courier accounts created and maintained exclusively by SuperAdmin.
                </p>
              </div>

              <div className="cm-toolbar">
                <div className="cm-input-wrapper" style={{ width: '260px' }}>
                  <Search size={15} className="cm-input-icon" />
                  <input
                    type="text"
                    placeholder="Search by name, GST, contact..."
                    value={courierSearch}
                    onChange={(e) => {
                      setCourierSearch(e.target.value);
                      setCourierPage(1);
                    }}
                    className="cm-input"
                  />
                </div>

                <select
                  value={courierStatusFilter}
                  onChange={(e) => {
                    setCourierStatusFilter(e.target.value);
                    setCourierPage(1);
                  }}
                  className="cm-select"
                >
                  <option value="ALL">All Status</option>
                  <option value="ACTIVE">Active Only</option>
                  <option value="INACTIVE">Inactive Only</option>
                </select>
              </div>
            </div>

            <div className="cm-table-responsive">
              <table className="cm-table">
                <thead>
                  <tr>
                    <th>Courier</th>
                    <th>Contact Person</th>
                    <th>Contact Info</th>
                    <th>Website</th>
                    <th style={{ textAlign: 'center' }}>Branches</th>
                    <th style={{ textAlign: 'center' }}>Districts</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCouriers.length === 0 ? (
                    <tr>
                      <td colSpan={8}>
                        <div className="cm-empty-table-state">
                          <Building2 size={36} color="#cbd5e1" />
                          <h4>No courier partners found</h4>
                          <p>Try clearing your search query or add a new courier company.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedCouriers.map((c) => {
                      const stats = courierStatsMap[c.courierId] || { branchCount: 0, districtCount: 0 };
                      const docCount = (c.documents || []).length;
                      return (
                        <tr key={c.courierId}>
                          {/* Courier Col */}
                          <td>
                            <div className="cm-cell-courier">
                              <div className="cm-courier-logo">
                                {c.logo ? (
                                  <img src={c.logo} alt="" />
                                ) : (
                                  <Building2 size={18} color="#8a6b32" />
                                )}
                              </div>
                              <div>
                                <strong className="cm-courier-info-title">{c.name}</strong>
                                <span className="cm-courier-id-tag">{c.courierId}</span>
                                {c.business?.gstNumber && (
                                  <span style={{ fontSize: '11px', color: '#64748b', marginLeft: '6px' }}>
                                    GST: {c.business.gstNumber}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Contact Person */}
                          <td>
                            <div className="cm-cell-contact">
                              <strong>{c.contactPerson}</strong>
                            </div>
                          </td>

                          {/* Contact Info */}
                          <td>
                            <div className="cm-cell-contact">
                              <span>
                                <Phone size={12} /> {c.contactNumber}
                              </span>
                              <span>
                                <Mail size={12} /> {c.email}
                              </span>
                            </div>
                          </td>

                          {/* Website */}
                          <td>
                            {c.website ? (
                              <a
                                href={c.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="cm-link-clean"
                              >
                                Visit <ExternalLink size={12} />
                              </a>
                            ) : (
                              <span style={{ color: '#94a3b8' }}>—</span>
                            )}
                          </td>

                          {/* Branches Count */}
                          <td style={{ textAlign: 'center', fontWeight: '700' }}>
                            {stats.branchCount}
                          </td>

                          {/* Districts Count */}
                          <td style={{ textAlign: 'center', fontWeight: '700', color: '#8a6b32' }}>
                            {stats.districtCount}
                          </td>

                          {/* Status */}
                          <td>
                            <span
                              className={`cm-badge ${
                                c.active !== false ? 'cm-badge-success' : 'cm-badge-inactive'
                              }`}
                            >
                              {c.active !== false ? 'Active' : 'Inactive'}
                            </span>
                          </td>

                          {/* Actions */}
                          <td>
                            <div className="cm-table-actions" style={{ justifyContent: 'flex-end' }}>
                              <button
                                type="button"
                                className="cm-btn-icon"
                                title="Edit Courier Company"
                                onClick={() => {
                                  setEditingCourier(c);
                                  setIsCourierModalOpen(true);
                                }}
                              >
                                <Edit2 size={14} />
                              </button>

                              <button
                                type="button"
                                className="cm-btn-secondary cm-btn-xs"
                                title="Add Authorized Branch"
                                onClick={() => {
                                  setEditingBranch(null);
                                  setBranchCourierTargetId(c.courierId);
                                  setIsBranchModalOpen(true);
                                }}
                              >
                                <GitBranch size={12} /> + Branch
                              </button>

                              <button
                                type="button"
                                className="cm-btn-secondary cm-btn-xs"
                                title="Manage Business Documents"
                                onClick={() => {
                                  setDocModalEntity({
                                    type: 'COURIER',
                                    id: c.courierId,
                                    name: c.name,
                                    docs: c.documents || []
                                  });
                                  setIsDocModalOpen(true);
                                }}
                              >
                                <FileText size={12} /> Docs ({docCount})
                              </button>

                              <button
                                type="button"
                                className={`cm-btn-icon ${
                                  c.active !== false ? '' : 'cm-btn-icon-danger'
                                }`}
                                title={c.active !== false ? 'Deactivate Courier' : 'Activate Courier'}
                                onClick={() => handleToggleCourierActive(c)}
                              >
                                <Power size={14} color={c.active !== false ? '#16a34a' : '#dc2626'} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredCouriers.length > itemsPerPage && (
              <div className="cm-pagination">
                <span>
                  Showing {(courierPage - 1) * itemsPerPage + 1} to{' '}
                  {Math.min(courierPage * itemsPerPage, filteredCouriers.length)} of{' '}
                  {filteredCouriers.length} couriers
                </span>
                <div className="cm-pagination-btns">
                  <button
                    type="button"
                    className="cm-btn-secondary cm-btn-xs"
                    disabled={courierPage === 1}
                    onClick={() => setCourierPage((p) => p - 1)}
                  >
                    <ChevronLeft size={14} /> Prev
                  </button>
                  <button
                    type="button"
                    className="cm-btn-secondary cm-btn-xs"
                    disabled={courierPage * itemsPerPage >= filteredCouriers.length}
                    onClick={() => setCourierPage((p) => p + 1)}
                  >
                    Next <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═════════════════════════════════════════
            TAB 2: AUTHORIZED BRANCHES
            ═════════════════════════════════════════ */}
        {activeTab === 'branches' && (
          <div className="cm-card">
            <div className="cm-card-header">
              <div>
                <h3 className="cm-card-title">Authorized Branches</h3>
                <p className="cm-card-subtitle">
                  Authorized branch master records with branch codes, addresses, and contacts.
                </p>
              </div>

              <div className="cm-toolbar">
                <div className="cm-input-wrapper" style={{ width: '220px' }}>
                  <Search size={15} className="cm-input-icon" />
                  <input
                    type="text"
                    placeholder="Search branch, code, city..."
                    value={branchSearch}
                    onChange={(e) => {
                      setBranchSearch(e.target.value);
                      setBranchPage(1);
                    }}
                    className="cm-input"
                  />
                </div>

                <select
                  value={branchCourierFilter}
                  onChange={(e) => {
                    setBranchCourierFilter(e.target.value);
                    setBranchPage(1);
                  }}
                  className="cm-select"
                >
                  <option value="ALL">All Couriers</option>
                  {uniqueCouriers.map((c) => (
                    <option key={c.courierId} value={c.courierId}>
                      {c.name}
                    </option>
                  ))}
                </select>

                <select
                  value={branchStatusFilter}
                  onChange={(e) => {
                    setBranchStatusFilter(e.target.value);
                    setBranchPage(1);
                  }}
                  className="cm-select"
                >
                  <option value="ALL">All Status</option>
                  <option value="ACTIVE">Active Only</option>
                  <option value="INACTIVE">Inactive Only</option>
                </select>

                <button
                  type="button"
                  className="cm-btn-primary cm-btn-sm"
                  onClick={() => {
                    setEditingBranch(null);
                    setBranchCourierTargetId(uniqueCouriers[0]?.courierId || '');
                    setIsBranchModalOpen(true);
                  }}
                >
                  <Plus size={14} /> Add Branch
                </button>
              </div>
            </div>

            <div className="cm-table-responsive">
              <table className="cm-table">
                <thead>
                  <tr>
                    <th>Branch Name</th>
                    <th>Courier Partner</th>
                    <th>Branch Code</th>
                    <th>Contact Person</th>
                    <th>Branch Contact</th>
                    <th>Branch District</th>
                    <th>Address</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedBranches.length === 0 ? (
                    <tr>
                      <td colSpan={10}>
                        <div className="cm-empty-table-state">
                          <GitBranch size={36} color="#cbd5e1" />
                          <h4>No authorized branches found</h4>
                          <p>Add branches to courier partners to start assigning delivery districts.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedBranches.map((b) => {
                      const distCount = branchDistrictsCountMap[b.branchId] || 0;
                      const docCount = (b.documents || []).length;
                      const branchDistrict =
                        b.address?.districtName ||
                        b.address?.districtCode ||
                        b.districtName ||
                        b.district ||
                        b.address?.city ||
                        '—';
                      const branchState =
                        b.address?.stateName ||
                        b.address?.stateCode ||
                        b.stateName ||
                        b.state ||
                        '';
                      return (
                        <tr key={b.branchId}>
                          <td>
                            <strong style={{ color: '#0f172a' }}>{b.branchName}</strong>
                          </td>
                          <td>
                            <span style={{ fontWeight: '600', color: '#64748b' }}>
                              {b.courierName || b.courierId}
                            </span>
                          </td>
                          <td>
                            <span className="cm-courier-id-tag">{b.branchCode}</span>
                          </td>
                          <td>{b.contactPerson}</td>
                          <td>
                            <div className="cm-cell-contact">
                              <strong>
                                <Phone size={12} style={{ display: 'inline', marginRight: '4px' }} />
                                {b.contactNumber}
                              </strong>
                              {b.alternateContactNumber && (
                                <span style={{ fontSize: '11px', color: '#64748b' }}>
                                  Alt: {b.alternateContactNumber}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="cm-branch-district-cell">
                              <span className="cm-district-badge">
                                <MapPin size={12} style={{ marginRight: '4px', color: '#8a6b32' }} />
                                <strong>{branchDistrict}</strong>
                              </span>
                              {branchState && (
                                <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '2px' }}>
                                  {branchState}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div style={{ maxWidth: '200px', fontSize: '12.5px', color: '#475569' }}>
                              {b.address?.addressLine1 && <div>{b.address.addressLine1}</div>}
                              <div>
                                {b.address?.city ? `${b.address.city}` : ''}
                                {b.address?.pinCode ? ` (${b.address.pinCode})` : ''}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span
                              className={`cm-badge ${
                                b.active !== false ? 'cm-badge-success' : 'cm-badge-inactive'
                              }`}
                            >
                              {b.active !== false ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <div className="cm-table-actions" style={{ justifyContent: 'flex-end' }}>
                              <button
                                type="button"
                                className="cm-btn-secondary cm-btn-xs"
                                title="Assign Service Districts"
                                onClick={() => {
                                  const parentC = couriers.find((c) => c.courierId === b.courierId);
                                  setTargetBranchForDistricts({ branch: b, courier: parentC });
                                  setIsDistrictModalOpen(true);
                                }}
                              >
                                <MapPin size={12} /> Coverage ({distCount})
                              </button>

                              <button
                                type="button"
                                className="cm-btn-secondary cm-btn-xs"
                                title="Branch Documents"
                                onClick={() => {
                                  setDocModalEntity({
                                    type: 'BRANCH',
                                    id: b.branchId,
                                    courierId: b.courierId,
                                    name: `${b.branchName} (${b.branchCode})`,
                                    docs: b.documents || []
                                  });
                                  setIsDocModalOpen(true);
                                }}
                              >
                                <FileText size={12} /> Docs ({docCount})
                              </button>

                              <button
                                type="button"
                                className="cm-btn-icon"
                                title="Edit Branch"
                                onClick={() => {
                                  setEditingBranch(b);
                                  setBranchCourierTargetId(b.courierId);
                                  setIsBranchModalOpen(true);
                                }}
                              >
                                <Edit2 size={13} />
                              </button>

                              <button
                                type="button"
                                className="cm-btn-icon"
                                title={b.active !== false ? 'Deactivate Branch' : 'Activate Branch'}
                                onClick={() => handleToggleBranchActive(b)}
                              >
                                <Power size={13} color={b.active !== false ? '#16a34a' : '#dc2626'} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredBranches.length > itemsPerPage && (
              <div className="cm-pagination">
                <span>
                  Showing {(branchPage - 1) * itemsPerPage + 1} to{' '}
                  {Math.min(branchPage * itemsPerPage, filteredBranches.length)} of{' '}
                  {filteredBranches.length} branches
                </span>
                <div className="cm-pagination-btns">
                  <button
                    type="button"
                    className="cm-btn-secondary cm-btn-xs"
                    disabled={branchPage === 1}
                    onClick={() => setBranchPage((p) => p - 1)}
                  >
                    <ChevronLeft size={14} /> Prev
                  </button>
                  <button
                    type="button"
                    className="cm-btn-secondary cm-btn-xs"
                    disabled={branchPage * itemsPerPage >= filteredBranches.length}
                    onClick={() => setBranchPage((p) => p + 1)}
                  >
                    Next <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═════════════════════════════════════════
            TAB 3: SERVICE COVERAGE (SECTION 19)
            ═════════════════════════════════════════ */}
        {activeTab === 'coverage' && (
          <div className="cm-card">
            <div className="cm-coverage-controls">
              <div className="cm-coverage-state-select">
                <label className="cm-label" style={{ margin: 0 }}>
                  Select State:
                </label>
                <select
                  value={coverageState}
                  onChange={(e) => {
                    setCoverageState(e.target.value);
                    setCoverageSearch('');
                  }}
                  className="cm-select"
                  style={{ minWidth: '220px', fontWeight: '600' }}
                >
                  {(stateDistrictData?.states || []).map((s) => (
                    <option key={s.state} value={s.state}>
                      {s.state}
                    </option>
                  ))}
                </select>
              </div>

              <div className="cm-coverage-stat-strip">
                <div className="cm-coverage-stat-item">
                  Total Districts: <strong>{coverageStateDistricts.length}</strong>
                </div>
                <div className="cm-coverage-stat-item">
                  Covered: <strong style={{ color: '#059669' }}>{stateCoveredCount}</strong>
                </div>
                <div className="cm-coverage-stat-item">
                  Uncovered:{' '}
                  <strong style={{ color: '#d97706' }}>
                    {coverageStateDistricts.length - stateCoveredCount}
                  </strong>
                </div>
                <div className="cm-coverage-stat-item">
                  Coverage:{' '}
                  <strong style={{ color: '#8a6b32' }}>
                    {coverageStateDistricts.length > 0
                      ? `${Math.round((stateCoveredCount / coverageStateDistricts.length) * 100)}%`
                      : '0%'}
                  </strong>
                </div>
              </div>

              <div className="cm-input-wrapper" style={{ width: '220px' }}>
                <Search size={15} className="cm-input-icon" />
                <input
                  type="text"
                  placeholder="Filter district..."
                  value={coverageSearch}
                  onChange={(e) => setCoverageSearch(e.target.value)}
                  className="cm-input"
                />
              </div>
            </div>

            <div className="cm-table-responsive">
              <table className="cm-table">
                <thead>
                  <tr>
                    <th>District Name</th>
                    <th>State</th>
                    <th style={{ textAlign: 'center' }}>Active Courier Branches Serving</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCoverageDistricts.length === 0 ? (
                    <tr>
                      <td colSpan={5}>
                        <div className="cm-empty-table-state">
                          <MapPin size={36} color="#cbd5e1" />
                          <h4>No districts matching "{coverageSearch}"</h4>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredCoverageDistricts.map((dist) => {
                      const branchCount = coverageDistrictBranchCounts[dist] || 0;
                      return (
                        <tr
                          key={dist}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleOpenDistrictCoverageDetail(dist, coverageState)}
                        >
                          <td>
                            <strong style={{ color: '#0f172a', fontSize: '14px' }}>{dist}</strong>
                          </td>
                          <td>{coverageState}</td>
                          <td style={{ textAlign: 'center' }}>
                            <span
                              style={{
                                fontSize: '15px',
                                fontWeight: '700',
                                color: branchCount > 0 ? '#8a6b32' : '#94a3b8'
                              }}
                            >
                              {branchCount}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`cm-badge ${
                                branchCount > 0 ? 'cm-badge-success' : 'cm-badge-inactive'
                              }`}
                            >
                              {branchCount > 0 ? 'Covered' : 'Uncovered'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <button
                              type="button"
                              className="cm-btn-secondary cm-btn-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDistrictCoverageDetail(dist, coverageState);
                              }}
                            >
                              <Eye size={13} /> View Branches
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ═════════════════════════════════════════
            TAB 4: ORDER ASSIGNMENT SIMULATOR
            ═════════════════════════════════════════ */}
        {activeTab === 'simulator' && (
          <div className="cm-card">
            <OrderAssignmentSimulator
              couriers={uniqueCouriers}
              branches={uniqueBranches}
              serviceAreas={serviceAreas}
            />
          </div>
        )}

        {/* ═════════════════════════════════════════
            TAB 5: AUDIT HISTORY (SECTION 22)
            ═════════════════════════════════════════ */}
        {activeTab === 'audit' && (
          <div className="cm-card">
            <div className="cm-card-header">
              <div>
                <h3 className="cm-card-title">Administrative Audit Trail</h3>
                <p className="cm-card-subtitle">
                  Historical log of courier creation, branch updates, activations, and district
                  assignments.
                </p>
              </div>
              <button
                type="button"
                className="cm-btn-secondary cm-btn-sm"
                onClick={async () => {
                  const logs = await getAuditLogs(50);
                  setAuditLogs(logs);
                  showToast('Refreshed audit history');
                }}
              >
                <RefreshCw size={14} /> Refresh Logs
              </button>
            </div>

            <div className="cm-table-responsive">
              <table className="cm-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Actor</th>
                    <th>Action</th>
                    <th>Entity Type</th>
                    <th>Entity ID</th>
                    <th>Details / Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.length === 0 ? (
                    <tr>
                      <td colSpan={6}>
                        <div className="cm-empty-table-state">
                          <History size={36} color="#cbd5e1" />
                          <h4>No audit history entries logged yet</h4>
                          <p>
                            Administrative activities such as adding couriers or assigning districts
                            will be recorded here.
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    auditLogs.map((log, idx) => {
                      let actionClass = 'cm-action-update';
                      if (log.action?.includes('CREATE')) actionClass = 'cm-action-create';
                      if (log.action?.includes('DEACTIVATE') || log.action?.includes('REMOVE'))
                        actionClass = 'cm-action-delete';
                      if (log.action?.includes('ACTIVATE')) actionClass = 'cm-action-toggle';

                      return (
                        <tr key={log.logId || idx}>
                          <td style={{ fontSize: '12px', color: '#64748b' }}>
                            {log.createdAt
                              ? new Date(log.createdAt).toLocaleString()
                              : 'Recent'}
                          </td>
                          <td>
                            <strong>{log.actor || 'SUPERADMIN'}</strong>
                          </td>
                          <td>
                            <span className={`cm-audit-action-tag ${actionClass}`}>
                              {log.action}
                            </span>
                          </td>
                          <td>{log.entityType}</td>
                          <td>
                            <span className="cm-courier-id-tag">{log.entityId}</span>
                          </td>
                          <td style={{ fontSize: '12.5px', color: '#475569' }}>
                            {log.notes || (log.newValue ? JSON.stringify(log.newValue) : '—')}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ═════════════════════════════════════════
            MODALS
            ═════════════════════════════════════════ */}
        {/* 1. Add / Edit Courier Modal */}
        <CourierFormModal
          isOpen={isCourierModalOpen}
          onClose={() => setIsCourierModalOpen(false)}
          onSave={handleSaveCourier}
          editingCourier={editingCourier}
        />

        {/* 2. Add / Edit Branch Modal */}
        <BranchFormModal
          isOpen={isBranchModalOpen}
          onClose={() => setIsBranchModalOpen(false)}
          onSave={handleSaveBranch}
          couriers={uniqueCouriers}
          selectedCourierId={branchCourierTargetId}
          editingBranch={editingBranch}
        />

        {/* 3. Assign Districts Modal */}
        <BranchDistrictModal
          isOpen={isDistrictModalOpen}
          onClose={() => setIsDistrictModalOpen(false)}
          branch={targetBranchForDistricts?.branch}
          courier={targetBranchForDistricts?.courier}
          currentAssignedServiceAreas={serviceAreas}
          onSaveAssignments={handleSaveDistrictAssignments}
        />

        {/* 4. Reusable Document Manager Modal */}
        <DocumentManagerModal
          isOpen={isDocModalOpen}
          onClose={() => setIsDocModalOpen(false)}
          entityType={docModalEntity?.type || 'COURIER'}
          entityTitle={docModalEntity?.name || ''}
          documents={docModalEntity?.docs || []}
          onSaveDocuments={handleSaveDocuments}
        />

        {/* 5. District Detail View Modal */}
        <DistrictDetailModal
          isOpen={isDistrictDetailOpen}
          onClose={() => setIsDistrictDetailOpen(false)}
          districtName={selectedDistrictDetail.districtName}
          stateName={selectedDistrictDetail.stateName}
          eligibleBranches={selectedDistrictDetail.branches}
        />
      </div>
    </Layout>
  );
};

export default CourierManagement;
